import { getShortName } from '@/utils/userNames'
import EmptyState from '@components/EmptyState'
import { GetExpenseResponse, GetPayUpType, GetUserType } from '@orbit/api-types'
import React from 'react'
import { ActivityDateGroup, ActivityPerson } from '../balancesFormat'
import { ActivityGrid, ActivityList, DateGroup, DateHeader } from './ActivitySection.styles'
import ExpenseCard from './ExpenseCard'
import PayUpCard from './PayUpCard'

interface Props {
	groupedActivity: ActivityDateGroup[]
	/** Everyone the cards may need to name: trip members or public travelers. */
	people: ActivityPerson[]
	/** The signed-in viewer; undefined on the public surface. */
	me: GetUserType | undefined
	/** Receipt thumbnails by file id; pass an empty map when not resolvable. */
	fileUrlById: Map<string, string>
	showCurrencyCode: boolean
	/** Zero-state copy — the feed picks the art and layout itself. */
	empty: { title: string; description?: string }
	/** Omit both handlers to render the feed read-only (public surface). */
	onClickExpense?: (expense: GetExpenseResponse) => void
	onClickPayUp?: (payUp: GetPayUpType) => void
}

/**
 * THE activity list — the date-grouped expense/pay-up card feed. The single
 * rendering of trip money activity: the member Balances page wraps it in
 * `ActivitySection` (filter/export/add header, click-through modals) and the
 * public expenses page renders it read-only, so the two surfaces cannot drift.
 */
const ActivityFeed: React.FC<Props> = ({
	groupedActivity,
	people,
	me,
	fileUrlById,
	showCurrencyCode,
	empty,
	onClickExpense,
	onClickPayUp,
}) => {
	const personById = (id: number): ActivityPerson | undefined => people.find(p => p.id === id)

	const personFullName = (userId: number): string => {
		const person = personById(userId)
		if (!person) return 'Unknown'
		return [person.firstName, person.lastName].filter(Boolean).join(' ')
	}

	const personShortName = (userId: number): string => getShortName(personById(userId))

	const expenseThumbnail = (expense: GetExpenseResponse): string | undefined => {
		const firstId = expense.fileIds?.[0]
		if (!firstId) return undefined
		return fileUrlById.get(firstId)
	}

	if (groupedActivity.length === 0) {
		return (
			<EmptyState
				art="expenses"
				variant="panel"
				title={empty.title}
				description={empty.description}
			/>
		)
	}

	return (
		<ActivityList>
			{groupedActivity.map(group => (
				<DateGroup key={group.dateKey}>
					<DateHeader>{group.dateKey}</DateHeader>
					<ActivityGrid>
						{group.items.map(item =>
							item.kind === 'expense' ? (
								<ExpenseCard
									key={`expense-${item.data.id}`}
									expense={item.data}
									me={me}
									payer={personById(item.data.paidByUserId)}
									payerShortName={personShortName(item.data.paidByUserId)}
									payerFullName={personFullName(item.data.paidByUserId)}
									thumbnailUrl={expenseThumbnail(item.data)}
									showCurrencyCode={showCurrencyCode}
									onClick={onClickExpense && (() => onClickExpense(item.data))}
								/>
							) : (
								<PayUpCard
									key={`payup-${item.data.id}`}
									payUp={item.data}
									me={me}
									fromUser={personById(item.data.fromUserId)}
									toUser={personById(item.data.toUserId)}
									fromShortName={personShortName(item.data.fromUserId)}
									fromFullName={personFullName(item.data.fromUserId)}
									toShortName={personShortName(item.data.toUserId)}
									toFullName={personFullName(item.data.toUserId)}
									showCurrencyCode={showCurrencyCode}
									onClick={onClickPayUp && (() => onClickPayUp(item.data))}
								/>
							),
						)}
					</ActivityGrid>
				</DateGroup>
			))}
		</ActivityList>
	)
}

export default ActivityFeed
